import { useMemo, useState } from "react";
import { Lightbulb } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";
import { useCurrentUser } from "@/hooks/useCurrentUser";

import { AgentGrid } from "../components/AgentGrid";
import { CategoryFilter } from "../components/CategoryFilter";
import { SuggestionModal } from "../components/SuggestionModal";
import { agents, categories } from "../data/agents";
import { AgentCategory, ONBOARDING_AGENT_ID } from "../types";

export const StaceyLibraryPage = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useCurrentUser();
  const [suggestionModalOpen, setSuggestionModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] =
    useState<AgentCategory | null>(null);

  const isOnboarded = currentUser?.onboarded ?? false;

  const filteredAgents = useMemo(() => {
    let result = agents;

    // For onboarded users, hide the onboarding agent
    if (isOnboarded) {
      result = result.filter((agent) => agent.id !== ONBOARDING_AGENT_ID);
    }

    // Apply category filter
    if (selectedCategory !== null) {
      result = result.filter((agent) => agent.category === selectedCategory);
    }

    return result;
  }, [selectedCategory, isOnboarded]);

  const handleTryNow = (agentId: string) => {
    const agent = agents.find((a) => a.id === agentId);
    if (agent) {
      navigate("/chat", { state: { initialPrompt: agent.prompt } });
    } else {
      navigate("/chat");
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <Typography size="h2">Stacey Agents</Typography>
          <Typography size="b2" className="text-[#767677] mt-1">
            Your AI-powered toolkit for building and running your consulting
            business
          </Typography>
        </div>

        {/* Agents List with Filter */}
        <div>
          <CategoryFilter
            categories={categories}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
          />
          <div className="bg-[#f9f9f9] rounded-b-2xl px-4 md:px-6 pb-6 pt-4 md:pt-6">
            <AgentGrid
              agents={filteredAgents}
              onTryNow={handleTryNow}
              isOnboarded={isOnboarded}
            />
          </div>
        </div>

        {/* Suggestion Section */}
        <div className="flex flex-col items-start py-6">
          <Lightbulb
            size={40}
            weight="regular"
            className="text-gray-400 mb-3"
          />
          <Typography size="b2" className="text-gray-600">
            Didn't find what you're looking for?
          </Typography>
          <Typography size="b2" className="text-gray-600 mb-3">
            Let us know what you'd like Stacey to do
          </Typography>
          <Button
            onClick={() => setSuggestionModalOpen(true)}
            className="bg-gray-900 text-white hover:bg-gray-800"
          >
            Suggest an Agent
          </Button>
        </div>
      </div>

      <SuggestionModal
        open={suggestionModalOpen}
        onOpenChange={setSuggestionModalOpen}
      />
    </DashboardLayout>
  );
};
